import { CheckOutlined, DownOutlined, UploadOutlined } from '@ant-design/icons';
import { CrudFilter, IResourceComponentsProps, useCreate, useNotification } from '@refinedev/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Modal,
  notification,
  Typography,
  Upload
} from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { UploadChangeParam, UploadFile } from 'antd/es/upload';
import dayjs from 'dayjs';
import { formatEther } from 'ethers';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useModalForm } from 'sunflower-antd';

import { createCompany, useGetInfoByUserCode } from '../../api/company';
import { useGetAllMetadataCurrency, useGetAvlBalanceInternal, useGetConfigurationFee, useGetExchangeRate } from '../../api/sendmoney';
import { httpClient, ROOT_URL } from '../../App';
import { ETHPrice, PaymentTypes } from '../../common/payment-types.const';
import {
  estimateMintGas,
  getPayTokenContract,
  mintTokens,
} from '../../common/smart-contracts/helpers/PayToken/PayToken';
import { PayToken } from '../../common/smart-contracts/typechain-types';
import {
  AutoComplete
} from '../../components/inputs/autocomplete';
import { Option } from '../../components/inputs/autocomplete/autocomplete';
import { AutocompleteCustomerInput } from '../../components/inputs/autocomplete/autocompleteCustomerInput';
import { getPurposeOfRemittanceOptionLabel, purposeOfRemittanceItems, purposeOfRemittancePlaceholder } from '../../components/inputs/select/purposeOfRemittance.constants';
import { SelectField } from '../../components/inputs/select/select';
import { PaymentConfirmationModal } from '../../components/modal/paymentConfirmationModal';
import { TOKEN_KEY } from '../../components/providers/authProvider';
import { IBankAccount } from '../../interfaces/bankAccounts';
import { IBank } from '../../interfaces/banks';
import { ICompany } from '../../interfaces/companies';
import { ICustomer } from '../../interfaces/customer';
import { allowedUploadTypes } from '../../types/allowedAttachedDocuments.type';
import PaymentStatus from '../../types/paymentStatus.type';
import { acceptUploadFileTypes } from '../../utils/acceptUploadFileTypes';
import { beforeUpload } from '../../utils/beforeUpload';
import { getETHPrice } from '../../utils/blockchain';
import { buildBankAccountValue, buildCurrencyValue, mapCompanyItem, mapCompanyUserItem, mapCurrencyItem } from '../../utils/edit.constants';
import {
  clearFieldErrorMessageOnChange,
  companyRules,
  companySearchFieldNameFilter,
  currencyRules,
  customerRules,
  getFileListFromEvent,
  paymentRequestServiceProviderRules
} from '../../utils/form.constants';
import { useIsAdminRole } from '../../utils/hooks/useIdentityAdmin.hook';
import { useNotificationOkCancel } from '../../utils/hooks/useNotificationOkCancel.hook';
import { confirmModalDifferentCurrenciesProps, confirmModalUnsavedChangesProps } from '../components/constants';
import Details from '../components/details/details';
import {
  companyBeneficiaryDetailsFields,
  companyDetailsFields,
  customerDetailsFieldsSmartUID
} from '../components/details/details.constants';
import { IPaymentFormValues } from '../components/interface';
import ICurrency from '../currencies/currency.interface';
import { inputFormStyles, uploadButtonStyle } from '../styles/style';

export const PaymentOrderCreatePage: React.FC<IResourceComponentsProps> = () => {
  const navigate = useNavigate();
  const { open: openNotification } = useNotification();
  const { mutate: createPaymentRequest, isLoading: isCreating } = useCreate();
  const isAdminRole = useIsAdminRole();

  const hasInputPaymentRef = useRef(false);
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const [showCompanyDetails, setShowCompanyDetails] = useState(false);
  const [showServiceProviderDetails, setShowServiceProviderDetails] = useState(false);
  const [nextStatus, setNextStatus] = useState<PaymentStatus>(PaymentStatus.DRAFT);
  const [paymentType] = useState<PaymentTypes>(PaymentTypes.Fiat);
  const [currencyData, setCurrencyData] = useState<any>();
  const [paymentCurrencyData, setPaymentCurrencyData] = useState<any>();
  const [customerData, setCustomerData] = useState<ICustomer>();
  const [companyData, setCompanyData] = useState<ICompany>();
  const [serviceProviderData, setServiceProviderData] = useState<ICompany>();
  const [bankData, setBankData] = useState<IBank | null>();
  const [accountData, setAccountData] = useState<IBankAccount>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [providerBankAccountData, setProviderBankAccountData] = useState<any>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [providerWalletData, setProviderWalletData] = useState<any>();
  const [amountData, setAmountData] = useState<string | null>(null);
  const [instructionsData, setInstructionsData] = useState<string | undefined>();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isFormEmpty, setIsFormEmpty] = useState(true);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [paymentConfirmationModalOpened, setPaymentConfirmationModalOpened] = useState<boolean>(false);
  const [api, alertContextHolder] = notification.useNotification({ placement: 'bottom' });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [userWallet, setUserWallet] = useState<string>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [payTokenContract, setPayTokenContract] = useState<PayToken>();
  const [fee, setFee] = useState<string>();
  const paymentConfirmed = useRef<(value: PromiseLike<null> | null) => void>();
  const paymentRejected = useRef<(reason?: any) => void>();
  const [ethPrice, setETHPrice] = useState<ETHPrice>();
  const [exchangeRate, setExchangeRate] = useState<number>(1);
  const [beneficiaryCurrencyId, setBeneficiaryCurrencyId] = useState<number | undefined>();
  const [paymentCurrencyId, setPaymentCurrencyId] = useState<number | undefined>();
  const [lastChangedField, setLastChangedField] = useState<'beneficiary' | 'payment'>('beneficiary');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [direction, setDirection] = useState<'BEN_TO_PAY' | 'PAY_TO_BEN'>('BEN_TO_PAY');
  const [configFee, setConfigFee] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [serviceFee, setServiceFee] = useState<number | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [feeConfigId, setFeeConfigId] = useState<number | undefined>(undefined);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [beneficiaryValueError, setBeneficiaryValueError] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [paymentValueError, setPaymentValueError] = useState(false);
  const [isChangingCurrency, setIsChangingCurrency] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [hasSetDefault, setHasSetDefault] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const [beneficiaryDecimal, setBeneficiaryDecimal] = useState(2);
  const [paymentDecimal, setPaymentDecimal] = useState(2);
  // Fees payable by is always BENEFICIARY (matching template)
  const feesPayer: 'SENDER' | 'BENEFICIARY' = 'BENEFICIARY';
  const [preservedBeneficiaryError, setPreservedBeneficiaryError] = useState<string | null>(null);

  const [customId, setCustomId] = useState<number | undefined | string>(undefined);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [beneficiaryCompanyId, setBeneficiaryCompanyId] = useState<number | undefined>(undefined);
  const [showReviewFee, setShowReviewFee] = useState(false)

  // Refs for tracking state (matching sample logic)
  const isSettingDefaultRef = useRef(false);
  const hasSetDefaultRef = useRef(false);
  const isChangingCurrencyRef = useRef(false);
  const hasUserEditedInputRef = useRef(false);
  const preservedBeneficiaryErrorRef = useRef<string | null>(null);

  // Currency state with symbols (matching sample)
  const [sendCurrency, setSendCurrency] = useState<number | undefined>(undefined);
  const [paymentCurrency, setPaymentCurrency] = useState<number | undefined>(undefined);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sendCurrencySymbol, setSendCurrencySymbol] = useState<string>('USD');
  const [paymentCurrencySymbol, setPaymentCurrencySymbol] = useState<string>('EUR');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [lastChange, setLastChange] = useState<'BENEFICIARY' | 'SENDER'>('BENEFICIARY');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [beneficiaryAmount, setBeneficiaryAmount] = useState<number>(0);
  const [specialInstructionsDraft, setSpecialInstructionsDraft] = useState<string | undefined>('');

  const amount = useWatch('amount', { form });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const beneficiaryValue = useWatch('beneficiary_value', { form });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const paymentValue = useWatch('payment_value', { form });
  const cryptoWallet = useWatch('cryptoWallet', { form });
  const currencyCode: string = useWatch('currencyCode', { form });

  // Get all currencies
  const { data: allCurrenciesData } = useGetAllMetadataCurrency();

  // Company Default states and hooks
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [companyDefaultSelected, setCompanyDefaultSelected] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [companyDefaultCurrency, setCompanyDefaultCurrency] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchDefaultCompany, setSearchDefaultCompany] = useState<any>();

  // Get companies default based on payment currency
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { dataCompaniesDefault, isLoading: loadingDefaulCompany } = useMetadataCompaniesDefault({
    currencyId: paymentCurrencyId || paymentCurrency,
    searchName: searchDefaultCompany,
  });

  // Get company default currency
  const { mutate: getCompanyDefault } = useGetCompanyDefaultMutation({
    onSuccess: (response: { data: [] }) => {
      setCompanyDefaultCurrency(response?.data);
    },
  });

  useEffect(() => {
    if (!paymentCurrencyId && !paymentCurrency) return;
    getCompanyDefault({ currencyId: paymentCurrencyId || paymentCurrency });
  }, [paymentCurrencyId, paymentCurrency, getCompanyDefault]);

  // Wallet Provider states and hooks
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [paramsWalletProvider, setParamsWalletProvider] = useState({ start: 0, end: 10 });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchWalletProvider, setSearchWalletProvider] = useState();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [walletSelectedProvider, setWalletSelectedProvider] = useState<any>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { dataListWalletProvider } = useWalletManagementProvider(
    Number(companyDefaultSelected?.id || 0),
    paramsWalletProvider,
    searchWalletProvider,
    paymentCurrencyId || paymentCurrency
  );

  // Bank Account Provider states and hooks
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [paramsBankAccountProvider, setParamsBankAccountProvider] = useState({ start: 0, end: 10 });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchBankAccountProvider, setSearchBankAccountProvider] = useState();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [bankAccountSelectedProvider, setBankAccountSelectedProvider] = useState<any>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { dataListBankAccountProvider } = useBankAccountManagementProvider(
    Number(companyDefaultSelected?.id || 0),
    paramsBankAccountProvider,
    searchBankAccountProvider,
    paymentCurrencyId || paymentCurrency
  );

  const { mutate: fetchInfoByUserCode } = useGetInfoByUserCode({
    onSuccess: () => {
      form.setFields([
        {
          name: "smartUID",
          errors: undefined,
        },
      ]);
    },
    onError: (error: { message: string; reason_code: string }) => {
      if (error?.reason_code === "ERR00010") {
        form.setFields([
          {
            name: "smartUID",
            errors: ["This SMART UID does not exist in SMART+"],
          },
        ]);
      } else {
        form.setFields([
          {
            name: "smartUID",
            errors: [error?.message || ""],
          },
        ]);
      }
    },
  });

  useEffect(() => {
    if (customId && paymentType === PaymentTypes.EWallet) {
      form.setFieldValue('smartUID', customId)
      fetchInfoByUserCode(customId);
    }
  }, [customId, paymentType, fetchInfoByUserCode, form]);

  useEffect(() => {
    if (customerData?.smartUID) {
      const specialInstructionsDraftData = `Ref ${customerData?.smartUID}`
      setSpecialInstructionsDraft(specialInstructionsDraftData);
      form.setFieldValue('specialInstructions', specialInstructionsDraftData);
    }
  }, [customerData?.smartUID, form]);

  const allCurrencies = useMemo(() => {
    const data = allCurrenciesData?.data?.data || [];
    const result = Array.isArray(data) ? data : [];
    return result;
  }, [allCurrenciesData]);

  // Custom mapCurrencyItem with icon and symbol for beneficiary currency
  const mapCurrencyUserItemWithIcon = useCallback((currency: ICurrency, index: number) => {
    const currencyId = (currency as any).id;
    // Check if currency id exists in allCurrencies list
    const currencyInList = allCurrencies.find((item: any) => item.id === currencyId);

    if (currencyInList) {
      // If found in list, display with icon and symbol
      const displaySymbol = currencyInList.symbol || currencyInList.code || buildCurrencyValue(currency);
      const isSelected = paymentCurrency === currencyId || paymentCurrencyId === currencyId;

      // Use symbol as value for display (form field will show symbol)
      // But keep currencyId in data for onSelect handler
      return {
        value: displaySymbol, // Use symbol as value so form field shows symbol
        key: String(currencyId) || index, // Use ID as key for uniqueness
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>

              <span style={{ fontWeight: 500 }}>{displaySymbol}</span>
            </div>
            {isSelected && (
              <CheckOutlined style={{ color: '#1890ff', fontSize: 16 }} />
            )}
          </div>
        ),
        data: currency, // Keep original currency data with ID
      };
    }

    // Otherwise use default mapping
    return mapCurrencyItem(currency, index);
  }, [allCurrencies, paymentCurrency, paymentCurrencyId]);

  // Get exchange rate - use sendCurrency and paymentCurrency (matching sample)
  // Only call API when customer is selected and both currencies have values
  const fromCurrency = paymentCurrency || paymentCurrencyId;
  const toCurrency = sendCurrency || beneficiaryCurrencyId;
  const shouldFetchRate = !!customerData?.id && !!fromCurrency && !!toCurrency;


  const { data: rateData } = useGetExchangeRate(
    { from: fromCurrency, to: toCurrency },
    {
      enabled: shouldFetchRate,
      onSuccess: (response: any) => {
        const rate = response?.data?.data || response?.data || 1;
        setExchangeRate(rate);
      },
    }
  );

  // Get available balance for Portfolio payment method
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: avlBalance, refetch: refetchBalance } = useGetAvlBalanceInternal(
    paymentCurrencyId || paymentCurrency || 0,
    customerData?.id || 0
  );

  useEffect(() => {
    // Since both currencies are the same, exchange rate is always 1
    // No need to recalculate values based on exchange rate changes
    if (beneficiaryCurrencyId && paymentCurrencyId && beneficiaryCurrencyId === paymentCurrencyId) {
      setExchangeRate(1);
    } else if (rateData?.data?.data) {
      const newRate = rateData.data.data;
      if (newRate !== exchangeRate) {
        setExchangeRate(newRate);
        // Recalculate values when exchange rate changes (only if currencies are different)
        if (!isChangingCurrency && !isSettingDefault && beneficiaryCurrencyId && paymentCurrencyId) {
          const currentBeneficiaryValue = form.getFieldValue('beneficiary_value');
          const currentPaymentValue = form.getFieldValue('payment_value');
          const beneficiaryValueNum = Number(currentBeneficiaryValue) || 0;
          const paymentValueNum = Number(currentPaymentValue) || 0;

          if (lastChangedField === 'beneficiary' && beneficiaryValueNum > 0) {
            // Recalculate payment value based on beneficiary value
            const paymentCurrency = allCurrencies.find((item: any) => item.id === paymentCurrencyId);
            if (paymentCurrency) {
              const calculatedPayment = (beneficiaryValueNum / newRate).toFixed(paymentCurrency.decimals || 2);

              form.setFieldValue('payment_value', calculatedPayment);
              setPaymentAmount(Number(calculatedPayment));
              // Trigger validation after recalculation
              setTimeout(() => {
                form.validateFields(['payment_value']).catch(() => {
                  // Validation errors are expected - ignore
                });
              }, 100);
            }
          } else if (lastChangedField === 'payment' && paymentValueNum > 0) {
            // Recalculate beneficiary value based on payment value
            const beneficiaryCurrency = allCurrencies.find((item: any) => item.id === beneficiaryCurrencyId);
            if (beneficiaryCurrency) {
              const calculatedBeneficiary = (paymentValueNum * newRate).toFixed(beneficiaryCurrency.decimals || 2);

              form.setFieldValue('beneficiary_value', calculatedBeneficiary);
              setBeneficiaryAmount(Number(calculatedBeneficiary));
              // Trigger validation after recalculation
              setTimeout(() => {
                form.validateFields(['beneficiary_value']).catch(() => {
                  // Validation errors are expected - ignore
                });
              }, 100);
            }
          }
        }
      }
    } else if (rateData?.data) {
      const newRate = rateData.data;
      if (newRate !== exchangeRate) {
        setExchangeRate(newRate);
        // Recalculate values when exchange rate changes (only if currencies are different)
        if (!isChangingCurrency && !isSettingDefault && beneficiaryCurrencyId && paymentCurrencyId) {
          const currentBeneficiaryValue = form.getFieldValue('beneficiary_value');
          const currentPaymentValue = form.getFieldValue('payment_value');
          const beneficiaryValueNum = Number(currentBeneficiaryValue) || 0;
          const paymentValueNum = Number(currentPaymentValue) || 0;

          if (lastChangedField === 'beneficiary' && beneficiaryValueNum > 0) {
            const paymentCurrency = allCurrencies.find((item: any) => item.id === paymentCurrencyId);
            if (paymentCurrency) {
              const calculatedPayment = (beneficiaryValueNum / newRate).toFixed(paymentCurrency.decimals || 2);

              form.setFieldValue('payment_value', calculatedPayment);
              setPaymentAmount(Number(calculatedPayment));
              // Trigger validation after recalculation
              setTimeout(() => {
                form.validateFields(['payment_value']).catch(() => {
                  // Validation errors are expected - ignore
                });
              }, 100);
            }
          } else if (lastChangedField === 'payment' && paymentValueNum > 0) {
            const beneficiaryCurrency = allCurrencies.find((item: any) => item.id === beneficiaryCurrencyId);
            if (beneficiaryCurrency) {
              const calculatedBeneficiary = (paymentValueNum * newRate).toFixed(beneficiaryCurrency.decimals || 2);

              form.setFieldValue('beneficiary_value', calculatedBeneficiary);
              setBeneficiaryAmount(Number(calculatedBeneficiary));
              // Trigger validation after recalculation
              setTimeout(() => {
                form.validateFields(['beneficiary_value']).catch(() => {
                  // Validation errors are expected - ignore
                });
              }, 100);
            }
          }
        }
      }
    }
  }, [rateData, exchangeRate, isChangingCurrency, isSettingDefault, beneficiaryCurrencyId, paymentCurrencyId, lastChangedField, allCurrencies, form]);

  // Get configuration fee
  const { mutate: fetchConfigurationFee, isLoading: loadingFee } = useGetConfigurationFee({
    onSuccess: (response: any) => {
      const feeData = response?.data?.data || response?.data;
      if (feeData) {
        setConfigFee(feeData);
      }
    },
    onError: () => {
      // Error handled by hook
    },
  });

  // Helper functions for rounding
  const roundTo = useCallback((value: number, decimals: number) => {
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
  }, []);

  const roundUp = useCallback((value: number, decimals: number) => {
    const factor = Math.pow(10, decimals);
    return Math.ceil(value * factor) / factor;
  }, []);

  const formatNumber = useCallback((value: number | string, decimals: number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0';
    return num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }, []);

  // Helper function to find currency by symbol
  const findCurrencyBySymbol = useCallback((symbol: string) => {
    return allCurrencies.find((item: any) => item.symbol === symbol || item.code === symbol);
  }, [allCurrencies]);

  // Get max limit for currency
  const getMaxLimit = useCallback((currencyId: number | undefined) => {
    if (!currencyId || !allCurrencies?.length) return null;
    const currency = allCurrencies.find((item: any) => item.id === currencyId);
    if (!currency) return null;
    const symbol = currency.symbol || currency.code;
    switch (symbol) {
      case 'ETH':
        return 10000000;
      case 'USDT':
      case 'DAI':
      case 'USD':
      case 'EUR':
        return 10000000000;
      case 'HKD':
        return 77800000000;
      case 'AUD':
        return 15000000000;
      case 'JPY':
        return 15479000000;
      case 'CHF':
        return 8000000000;
      case 'CNY':
        return 707000000000;
      case 'AED':
        return 36700000000;
      case 'SGD':
        return 12900000000;
      case 'GBP':
        return 7400000000;
      default:
        return null;
    }
  }, [allCurrencies]);

  // Handler functions matching sample logic
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleChangeBeneficiaryValue = useCallback((_e: React.ChangeEvent<HTMLInputElement>) => {
    // Mark that user has manually entered, no longer default
    isSettingDefaultRef.current = false;
    hasUserEditedInputRef.current = true;
    // Reset currency change flag when user manually changes value
    isChangingCurrencyRef.current = false;
    // Clear preserved error when user manually enters a new value
    preservedBeneficiaryErrorRef.current = null;
    setPreservedBeneficiaryError(null);
    setLastChange('BENEFICIARY');
    setLastChangedField('beneficiary');
    setDirection('BEN_TO_PAY');
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleChangePaymentValue = useCallback((_e: React.ChangeEvent<HTMLInputElement>) => {
    hasInputPaymentRef.current = true;
    // Mark that user has manually entered, no longer default
    isSettingDefaultRef.current = false;
    hasUserEditedInputRef.current = true;
    // Reset currency change flag when user manually changes value
    isChangingCurrencyRef.current = false;
    setLastChange('SENDER');
    setLastChangedField('payment');
    setDirection('PAY_TO_BEN');

    // Update beneficiary_value automatically (hidden field)
    const paymentValue = form.getFieldValue('payment_value');
    const paymentValueNum = Number(paymentValue) || 0;
    if (paymentValueNum > 0 && beneficiaryCurrencyId) {
      const beneficiaryValue = paymentValueNum.toFixed(beneficiaryDecimal);
      form.setFieldsValue({
        beneficiary_value: beneficiaryValue,
      });
      setBeneficiaryAmount(paymentValueNum);
      setPaymentAmount(paymentValueNum);
    }
  }, [form, beneficiaryCurrencyId, beneficiaryDecimal]);

  const handleBlurPayment = useCallback(async () => {
    hasInputPaymentRef.current = true;
    const rawValue = form.getFieldValue('payment_value');
    const val = Number(rawValue);

    if (val && val > 0) {
      setPaymentAmount(val);
      const formattedValue = val.toFixed(paymentDecimal);
      form.setFieldsValue({ payment_value: formattedValue });
      setDirection('PAY_TO_BEN');
      setLastChange('SENDER');
      setLastChangedField('payment');

      // Validate payment_value first
      try {
        await form.validateFields(['payment_value']);

        // If payment_value is valid, set beneficiary_value equal to it (since currencies are the same)
        // beneficiary_value is hidden but still needed for calculations
        if (beneficiaryCurrencyId) {
          // Since currencies are the same, beneficiary_value = payment_value
          const calculatedBeneficiaryValue = val.toFixed(beneficiaryDecimal);
          const calculatedBeneficiaryValueNum = val;

          form.setFieldsValue({
            beneficiary_value: calculatedBeneficiaryValue,
          });
          setBeneficiaryAmount(calculatedBeneficiaryValueNum);
        }
      } catch (error) {
        // If payment_value has error, reset beneficiary_value
        form.setFieldsValue({
          beneficiary_value: undefined,
        });
        setBeneficiaryAmount(0);
      }
    } else {
      form.setFieldsValue({
        beneficiary_value: undefined,
      });
      setPaymentAmount(0);
      setBeneficiaryAmount(0);
    }
  }, [form, paymentDecimal, beneficiaryDecimal, beneficiaryCurrencyId]);

  // Disable form fields until customer is selected
  // Customer selection is REQUIRED before any other fields can be filled
  const isFormDisabled = !customerData;

  useEffect(() => {
    if (!ethPrice && paymentType === PaymentTypes.EWallet) {
      (async () => {
        try {
          const priceResponse = await getETHPrice();
          if (priceResponse.status === 200) {
            setETHPrice(priceResponse.data);
          }
        } catch (error) {
          console.error('Error fetching ETH price:', error);
        }
      })();
    }
  }, [paymentType, ethPrice]);

  // useEffect(() => {
  //   if (paymentType === PaymentTypes.EWallet) {
  //     (async () => {
  //       try {
  //         const walletResponse = await getUserWallet();
  //         if (walletResponse.status === 200 && walletResponse.data?.wallet) {
  //           const wallet = walletResponse.data.wallet;
  //           setUserWallet(wallet);
  //           if (wallet) {
  //             const contract = await getPayTokenContract(wallet);
  //             if (contract) {
  //               setPayTokenContract(contract);
  //             }
  //           }
  //         }
  //       } catch (error) {
  //         console.error('Error fetching user wallet:', error);
  //       }
  //     })();
  //   }
  // }, [paymentType]);

  const calculateMintFee = useCallback(
    async (value: string) => {
      if (userWallet) {
        const contract = await getPayTokenContract(userWallet);
        const cryptoWallet = form.getFieldValue('cryptoWallet');
        if (contract && cryptoWallet) {
          const fee = await estimateMintGas(contract, cryptoWallet, value);
          if (fee) {
            setFee(formatEther(fee));
          }
        }
      }
    },
    [userWallet, form],
  );

  useEffect(() => {
    if (cryptoWallet && userWallet) {
      if (amount) {
        if (amount > 0) {
          calculateMintFee(amount.toString());
        } else {
          setFee('');
        }
      } else {
        setFee('');
      }
    }
  }, [userWallet, amount, cryptoWallet, calculateMintFee]);

  const mint = useCallback(async () => {
    const formData = form?.getFieldsValue();
    if (payTokenContract && userWallet && formData.cryptoWallet && formData?.amount) {
      try {
        await mintTokens(payTokenContract, formData.cryptoWallet, formData.amount.toString());
      } catch (e: any) {
        if (e?.info?.error?.code === -32000 || e?.info?.error?.data?.code === -32000) {
          api['error']({
            message: 'Transaction error',
            description: 'There is not enough funds on your balance',
          });
        } else if (e?.info?.error?.code === 4001 || e?.info?.error?.data?.code === 4001) {
          api['warning']({
            message: 'Operation cancelled',
            description: 'Transaction was cancelled by user',
          });
        } else {
          api['error']({
            message: 'Transaction error',
            description: 'Something went wrong',
          });
        }
        throw e;
      }
    } else {
      api['error']({
        message: 'Transaction error',
        description: 'Something went wrong',
      });
      return Promise.reject();
    }
  }, [form, userWallet, payTokenContract, api]);

  async function submitPayment() {
    if (paymentConfirmed.current && paymentRejected.current) {
      try {
        await mint();
        setPaymentConfirmationModalOpened(false);
        paymentConfirmed.current(null);
      } catch (e) {
        console.error(e);
        setPaymentConfirmationModalOpened(false);
        paymentRejected.current();
      }
    }
  }

  const clearCustomerData = useCallback(() => {
    setCustomerData(undefined);
    form.setFieldValue('customerId', undefined);
    // Clear all form fields when customer is cleared
    // This ensures a clean state when customer is removed
    form.resetFields([
      'dateOfTransaction',
      'beneficiary',
      'beneficiary_value',
      'beneficiary_currency',
      'beneficiary_wallet_address',
      'payment_value',
      'payment_currency',
      'companyId',
      'bankId',
      'bankAccountId',
      'specialInstructions',
      'purposeOfRemittance',
      'status',
      'documents',
      'cryptoWallet',
      'amount',
      'currencyCode',
      'providerBankAccount',
      'providerWallet',
      'serviceProviderId',
      'smartUID',
      'providerId',
      'providerCurrencyId',
      'providerCurrency',
      'providerCurrencyCode',
      'providerCurrencySymbol',
      'providerCurrencyName',
      'providerCurrencyDecimal',
      'providerCurrencyRate',
      'providerCurrencyRate',
    ]);
    // Reset related state
    setCurrencyData(undefined);
    setPaymentCurrencyData(undefined);
    setCompanyData(undefined);
    setBankData(undefined);
    setAccountData(undefined);
    setServiceProviderData(undefined);
    setBeneficiaryCurrencyId(undefined);
    setPaymentCurrencyId(undefined);
    setHasSetDefault(false);
    setFileList([]);
    setProviderBankAccountData(undefined);
    setProviderWalletData(undefined);
  }, [form]);

  const changeCustomerData = useCallback(
    async (data: ICustomer) => {
      clearCustomerData()
      setCustomerData(data);
      form.setFieldValue('customerId', data.fullName);

      // If customer is selected and currencies are loaded, set default currencies and values
      if (data.id && allCurrencies?.length) {

        // Find currencies directly by code - both use USD initially (matching template pattern)
        const beneficiaryCurrencyDefault = allCurrencies.find((item: any) => (item.code === 'USD' || item.symbol === 'USD') && item.id);

        // If not found with id, try without id check (fallback)
        const beneficiaryCurrencyFallback = beneficiaryCurrencyDefault || allCurrencies.find((item: any) => item.code === 'USD' || item.symbol === 'USD');

        if (!beneficiaryCurrencyFallback) {
          console.log('[FLOW] changeCustomerData - USD currency not found');
          return;
        }

        // Get IDs - try to get id from currency object
        // If currency object doesn't have id, we need to find it in allCurrencies by matching code
        let beneficiaryCurrencyId = beneficiaryCurrencyFallback.id;
        // Both currencies use the same ID initially (matching template)
        let paymentCurrencyId = beneficiaryCurrencyId;

        // If no id in the found currency, search allCurrencies for one with matching code and id
        if (!beneficiaryCurrencyId) {
          const currencyWithId = allCurrencies.find((item: any) =>
            item.id && (item.code === 'USD' || item.symbol === 'USD')
          );
          if (currencyWithId) {
            beneficiaryCurrencyId = currencyWithId.id;
            // Both currencies use the same ID (matching template)
            paymentCurrencyId = beneficiaryCurrencyId;
          }
        }

        if (!beneficiaryCurrencyId) {
          console.error('[FLOW] changeCustomerData - Currency ID is missing! Cannot proceed.');
          return;
        }

        // Set currencies in form and state FIRST
        // Use symbol for form field value to match mapCurrencyItemWithIcon
        // Both currencies use USD initially (matching template)
        const beneficiarySymbol = beneficiaryCurrencyFallback.symbol || beneficiaryCurrencyFallback.code;
        const paymentSymbol = beneficiaryCurrencyFallback.symbol || beneficiaryCurrencyFallback.code;
        form.setFieldsValue({
          beneficiary_currency: beneficiarySymbol,
          payment_currency: paymentSymbol,
        });

        // Set all currency states immediately - use fallback objects
        setCurrencyData(beneficiaryCurrencyFallback);
        setPaymentCurrencyData(beneficiaryCurrencyFallback); // Same currency for both
        setBeneficiaryCurrencyId(beneficiaryCurrencyId);
        setPaymentCurrencyId(paymentCurrencyId);
        setSendCurrency(beneficiaryCurrencyId);
        setPaymentCurrency(paymentCurrencyId);
        setSendCurrencySymbol(beneficiaryCurrencyFallback.symbol || beneficiaryCurrencyFallback.code);
        setPaymentCurrencySymbol(beneficiaryCurrencyFallback.symbol || beneficiaryCurrencyFallback.code);
        setBeneficiaryDecimal(beneficiaryCurrencyFallback.decimals || 2);
        setPaymentDecimal(beneficiaryCurrencyFallback.decimals || 2);
        // Verify currencies are set before calling API
        if (!beneficiaryCurrencyId) {
          console.log('[FLOW] changeCustomerData - Currencies not set, skipping API call');
          return;
        }

        // Since both currencies are the same, exchange rate is 1
        const newRate = 1;
        setExchangeRate(newRate);

        // Set default beneficiary value = 0
        const defaultBeneficiaryValue = '0.00';
        // // Set default beneficiary value = 1000
        // const defaultBeneficiaryValue = '1000.00';
        const defaultBeneficiaryValueNum = Number(defaultBeneficiaryValue);

        // Since currencies are the same, payment value = beneficiary value
        const calculatedPayment = defaultBeneficiaryValueNum.toFixed(beneficiaryCurrencyFallback.decimals || 2);
        const calculatedPaymentNum = Number(calculatedPayment);

        // Mark that we're setting default values
        isSettingDefaultRef.current = true;

        form.setFieldsValue({
          beneficiary_value: defaultBeneficiaryValue,
          payment_value: calculatedPayment,
        });
        setPaymentAmount(calculatedPaymentNum);
        setBeneficiaryAmount(defaultBeneficiaryValueNum);
        setLastChange('BENEFICIARY');
        setLastChangedField('beneficiary');
        setDirection('BEN_TO_PAY');

        // Reset flag after a short delay
        setTimeout(() => {
          isSettingDefaultRef.current = false;
        }, 0);
      }
    },
    [form, allCurrencies, clearCustomerData],
  );

  const handleFormValuesChange = useCallback((changedValues: any, allValues: any) => {
    const fields = Object.keys(allValues).filter(key => key !== 'dateOfTransaction');
    const filledFields = fields.filter(
      key => allValues[key] !== null && allValues[key] !== undefined && allValues[key] !== '',
    );
    setIsFormEmpty(filledFields.length === 0);
  }, []);

  useEffect(() => {
    form.setFieldValue('dateOfTransaction', dayjs());
    form.setFieldValue('status', PaymentStatus.DRAFT);
  }, [form]);

  // Auto-sync beneficiary_value with payment_value (hidden field)
  useEffect(() => {
    const currentPaymentValue = form.getFieldValue('payment_value');
    const currentBeneficiaryValue = form.getFieldValue('beneficiary_value');

    if (currentPaymentValue && beneficiaryCurrencyId && paymentCurrencyId) {
      const paymentValueNum = Number(currentPaymentValue);
      const beneficiaryValueNum = Number(currentBeneficiaryValue) || 0;

      // If currencies are the same, sync beneficiary_value with payment_value
      if (beneficiaryCurrencyId === paymentCurrencyId && paymentValueNum !== beneficiaryValueNum) {
        const formattedValue = paymentValueNum.toFixed(beneficiaryDecimal);
        form.setFieldsValue({
          beneficiary_value: formattedValue,
        });
        setBeneficiaryAmount(paymentValueNum);
      }
    }
  }, [paymentValue, beneficiaryCurrencyId, paymentCurrencyId, beneficiaryDecimal, form]);

  // Set default currencies (both USD initially, matching template pattern)
  useEffect(() => {
    if (!allCurrencies?.length) {
      return;
    }

    const beneficiaryCurrencyDefault = findCurrencyBySymbol('USD');
    // const paymentCurrencyDefault = findCurrencyBySymbol('EUR'); // Template uses same currency for both

    // Only proceed if we found the currency (avoid hardcoded fallback IDs)
    if (!beneficiaryCurrencyDefault) return;

    const beneficiaryCurrencyId = beneficiaryCurrencyDefault.id;
    // Both currencies use the same ID initially (matching template)
    const paymentCurrencyId = beneficiaryCurrencyId;

    // Set default currency values if not already set
    const currentBeneficiaryCurrency = form.getFieldValue('beneficiary_currency');
    const currentPaymentCurrency = form.getFieldValue('payment_currency');

    if (!currentBeneficiaryCurrency) {
      // Find currency by code (USD) and set its symbol as default
      const beneficiarySymbol = beneficiaryCurrencyDefault.symbol || beneficiaryCurrencyDefault.code;
      form.setFieldsValue({
        beneficiary_currency: beneficiarySymbol,
      });
      setCurrencyData(beneficiaryCurrencyDefault);
      setBeneficiaryCurrencyId(beneficiaryCurrencyId);
      setSendCurrency(beneficiaryCurrencyId);
      setSendCurrencySymbol(beneficiaryCurrencyDefault.symbol || beneficiaryCurrencyDefault.code);
      setBeneficiaryDecimal(beneficiaryCurrencyDefault.decimals || 2);
    }

    if (!currentPaymentCurrency) {
      // Both currencies use USD initially (matching template pattern)
      const paymentSymbol = beneficiaryCurrencyDefault.symbol || beneficiaryCurrencyDefault.code;
      form.setFieldsValue({
        payment_currency: paymentSymbol,
      });
      setPaymentCurrencyData(beneficiaryCurrencyDefault);
      setPaymentCurrencyId(paymentCurrencyId);
      setPaymentCurrency(paymentCurrencyId);
      setPaymentCurrencySymbol(beneficiaryCurrencyDefault.symbol || beneficiaryCurrencyDefault.code);
      setPaymentDecimal(beneficiaryCurrencyDefault.decimals || 2);
    }
  }, [allCurrencies, form, findCurrencyBySymbol]);

  useEffect(() => {
    if (!sendCurrency) return
    fetchConfigurationFee(sendCurrency)

    const intervalId = setInterval(() => {
      fetchConfigurationFee(sendCurrency)
    }, 60000)
    return () => clearInterval(intervalId)
  }, [sendCurrency, fetchConfigurationFee])

  // Set default values when all required data is loaded - matching sample logic
  useEffect(() => {
    if (!allCurrencies?.length) return;
    if (!paymentDecimal || paymentDecimal === 0) return;
    if (!beneficiaryDecimal || beneficiaryDecimal === 0) return;
    if (hasSetDefaultRef.current) {
      return; // Only set default once
    }

    const beneficiaryCurrencyDefault = findCurrencyBySymbol('USD');
    const paymentCurrencyDefault = findCurrencyBySymbol('EUR');

    if (!beneficiaryCurrencyDefault || !paymentCurrencyDefault) return;

    const beneficiaryCurrencyId = beneficiaryCurrencyDefault.id;
    const paymentCurrencyId = paymentCurrencyDefault.id;

    // Check if currencies are already set correctly
    const currentBeneficiaryCurrency = form.getFieldValue('beneficiary_currency');
    const currentPaymentCurrency = form.getFieldValue('payment_currency');

    // If currencies don't match defaults, don't set default values yet
    if (currentBeneficiaryCurrency && currentBeneficiaryCurrency !== beneficiaryCurrencyId) {
      hasSetDefaultRef.current = true;
      return;
    }
    if (currentPaymentCurrency && currentPaymentCurrency !== paymentCurrencyId) {
      hasSetDefaultRef.current = true;
      return;
    }

    // Check if user has already entered values manually
    const currentBeneficiaryValue = form.getFieldValue('beneficiary_value');
    const currentPaymentValue = form.getFieldValue('payment_value');
    const beneficiaryValueNum = Number(currentBeneficiaryValue) || 0;
    const paymentValueNum = Number(currentPaymentValue) || 0;

    // If user has already entered values, don't set default
    if (beneficiaryValueNum > 0 || paymentValueNum > 0) {
      hasSetDefaultRef.current = true;
      return;
    }

    // Set default beneficiary value to 1000 (as per requirement)
    // const defaultBeneficiaryValue = '1000.00';
    const defaultBeneficiaryValue = '0.00';
    // Since both currencies are the same, payment value = beneficiary value
    const paymentValue = Number(defaultBeneficiaryValue);

    // Set direction and lastChange to prevent recalculation in other useEffect
    setDirection('BEN_TO_PAY');
    setLastChange('BENEFICIARY');
    setLastChangedField('beneficiary');

    // Mark that we're setting default values
    isSettingDefaultRef.current = true;

    form.setFieldsValue({
      beneficiary_value: defaultBeneficiaryValue,
      payment_value: paymentValue,
    });
    setPaymentAmount(paymentValue);
    setBeneficiaryAmount(Number(defaultBeneficiaryValue));

    // Mark that default has been set and reset isSettingDefault flag
    hasSetDefaultRef.current = true;
    setTimeout(() => {
      isSettingDefaultRef.current = false;
    }, 0);
  }, [allCurrencies, rateData?.data?.data, rateData?.data, paymentDecimal, beneficiaryDecimal, exchangeRate, form, findCurrencyBySymbol]);

  // Calculate fees based on config fee
  useEffect(() => {
    if (!configFee || loadingFee || paymentType !== PaymentTypes.Fiat) return;
    if (isChangingCurrency) {
      return;
    }

    // Since currencies are the same, use paymentAmount for calculations
    // beneficiaryAmount = paymentAmount when currencies match
    const amountForFeeCalculation = paymentAmount || Number(paymentValue) || 0;

    // Fees payable by is always BENEFICIARY (matching template)
    // Calculate fees based on BENEFICIARY pays fee
    {
      // Beneficiary pays fee - fees are in payment currency (since currencies are the same)
      // Calculate service fee
      switch (configFee?.serviceFeeType) {
        case 'PERCENTAGE': {
          if (!amountForFeeCalculation) {
            setServiceFee(null);
          } else {
            setServiceFee(
              Number(
                roundUp(
                  ((amountForFeeCalculation || 0) * configFee?.serviceFee) / 100,
                  paymentDecimal
                )
              )
            );
          }
          break;
        }
        case 'FIXED':
          setServiceFee(
            Number(
              roundUp(
                configFee?.serviceFee * configFee?.usdToFeeCurrencyRate,
                paymentDecimal
              )
            )
          );
          break;
        default:
          setServiceFee(0);
      }

    }
  }, [
    configFee,
    paymentAmount,
    paymentValue,
    exchangeRate,
    loadingFee,
    beneficiaryCurrencyId,
    paymentCurrencyId,
    paymentType,
    allCurrencies,
    paymentDecimal,
    beneficiaryDecimal,
    feesPayer,
    isChangingCurrency,
    roundUp,
  ]);

  // Re-validate beneficiary_value after fee is fetched to ensure validation uses correct fee data
  useEffect(() => {
    if (!configFee || loadingFee || isChangingCurrency) {
      return;
    }

    // If beneficiary pays fee, wait for serviceFee to be calculated
    if (feesPayer === 'BENEFICIARY') {
      if (serviceFee === null) {
        return;
      }
    }

    const beneficiaryValue = form.getFieldValue('beneficiary_value');

    if (beneficiaryValue && Number(beneficiaryValue) > 0) {
      // Use setTimeout to ensure state is updated and avoid conflicts with other validations
      setTimeout(() => {
        if (!isChangingCurrency) {
          form.validateFields(['beneficiary_value']).then(() => {
            // Clear preserved error since validation passed with new fees
            setPreservedBeneficiaryError(null);
          }).catch(() => {
            // Error is expected if value is invalid, just ensure validation runs
          });
        }
      }, 200);
    }
  }, [configFee, loadingFee, paymentCurrencyId, beneficiaryCurrencyId, serviceFee, feesPayer, form, isChangingCurrency]);

  // Re-validate payment_value after fee is fetched to ensure validation uses correct fee data
  useEffect(() => {
    if (!configFee || loadingFee || isChangingCurrency) {
      return;
    }

    // If beneficiary pays fee, wait for serviceFee to be calculated
    if (feesPayer === 'BENEFICIARY') {
      if (serviceFee === null) {
        return;
      }
    }

    const paymentValue = form.getFieldValue('payment_value');

    if (paymentValue && Number(paymentValue) > 0) {
      // Use setTimeout to ensure state is updated and avoid conflicts with other validations
      setTimeout(() => {
        if (!isChangingCurrency) {
          form.validateFields(['payment_value']).then(() => {
            // Clear preserved error since validation passed with new fees
            preservedBeneficiaryErrorRef.current = null;
            setPreservedBeneficiaryError(null);
          }).catch(() => {
            // Error is expected if value is invalid, just ensure validation runs
          });
        }
      }, 200);
    }
  }, [configFee, loadingFee, paymentCurrencyId, beneficiaryCurrencyId, serviceFee, feesPayer, form, isChangingCurrency]);

  // Note: Auto-calculation is handled in onBlur handlers (handleBlurSend, handleBlurPayment)
  // This follows the doc requirement: "KHÔNG tính toán ngay → Chờ đến khi onBlur"

  // Sync fileList with form value
  useEffect(() => {
    const formDocuments = form.getFieldValue('documents');
    if (formDocuments && Array.isArray(formDocuments)) {
      setFileList(formDocuments);
    }
  }, [form]);

  const changeCompanyData = useCallback(
    (data: ICompany) => {
      if (data?.customId) {
        setCustomId(data?.customId);
      } else {
        setCustomId(undefined);
      }
      if (data?.id) {
        setBeneficiaryCompanyId(data?.id);
      } else {
        setBeneficiaryCompanyId(undefined);
      }
      if (data.id !== companyData?.id || data.id === undefined) {
        setCompanyData(data);
        form.setFieldValue('companyId', data.name);
        setAccountData(undefined);
        setBankData(undefined);
        form.validateFields(['companyId']);
        form.setFieldValue('bankId', '');
        form.setFieldValue('bankAccountId', '');
      }
    },
    [companyData?.id, form],
  );

  const onSelectCustomer = useCallback(
    (_: ICustomer, { data }: Option<ICustomer>) => {
      changeCustomerData(data);
    },
    [changeCustomerData],
  );

  const onSelectCompany = useCallback(
    (_: ICompany, { data }: Option<ICompany>) => {
      changeCompanyData(data);
    },
    [changeCompanyData],
  );

  const onSelectServiceProvider = useCallback((_: ICompany, { data }: Option<ICompany>) => {
    setServiceProviderData(data);
    form.setFieldValue('serviceProviderId', data?.name);
    // Reset provider bank account and wallet when provider changes
    setProviderBankAccountData(undefined);
    setProviderWalletData(undefined);
    form.setFieldValue('providerBankAccount', undefined);
    form.setFieldValue('providerWallet', undefined);
  }, [form]);

  const onSelectProviderBankAccount = useCallback((_: any, { data }: Option<any>) => {
    setProviderBankAccountData(data);
    form.setFieldValue('providerBankAccount', data?.accountNumber);
  }, [form]);

  const clearProviderBankAccountData = useCallback(() => {
    setProviderBankAccountData(undefined);
    form.setFieldValue('providerBankAccount', undefined);
  }, [form]);

  const onSelectProviderWallet = useCallback((_: any, { data }: Option<any>) => {
    setProviderWalletData(data);
    form.setFieldValue('providerWallet', data?.address);
  }, [form]);

  const clearProviderWalletData = useCallback(() => {
    setProviderWalletData(undefined);
    form.setFieldValue('providerWallet', undefined);
  }, [form]);

  const changeBankAccountData = useCallback(
    (data: IBankAccount) => {
      setBankData(data.bank);
      setAccountData(data);
      const displayValue = buildBankAccountValue(data);
      form.setFieldValue('beneficiary_bank_account', displayValue);
      form.setFieldValue('bankId', data.bank?.name);
      form.setFieldValue('bankAccountId', displayValue);
      form.validateFields(['beneficiary_bank_account', 'bankId', 'bankAccountId']);
    },
    [form],
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onSelectBankAccount = useCallback(
    (_: IBankAccount, { data }: Option<IBankAccount>) => {
      changeBankAccountData(data);
    },
    [changeBankAccountData],
  );

  const clearCurrencyData = useCallback(() => {
    setCurrencyData(undefined);
  }, []);

  const clearPaymentCurrencyData = useCallback(() => {
    setPaymentCurrencyData(undefined);
  }, []);

  const clearCompanyData = useCallback(() => {
    setCompanyData(undefined);
    setAccountData(undefined);
    setBankData(undefined);
    form.setFieldValue('bankId', '');
    form.setFieldValue('bankAccountId', '');
  }, [form]);

  const clearBankData = useCallback(() => {
    setBankData(undefined);
    setAccountData(undefined);
    form.setFieldValue('bankAccountId', '');
  }, [form]);

  const clearBankAccountData = useCallback(() => {
    setAccountData(undefined);
  }, []);

  const clearServiceProviderData = useCallback(() => {
    setServiceProviderData(undefined);
    form.setFieldValue('serviceProviderId', undefined);
  }, [form]);

  const onSubmit = useCallback(
    async (formValues: IPaymentFormValues) => {
      // Get userId from customerData (matching template logic)
      const userId = customerData?.id;

      // Get beneficiaryCurrency and paymentCurrency (matching template logic)
      const beneficiaryCurrency = sendCurrency || beneficiaryCurrencyId;
      const paymentCurrencyValue = paymentCurrency || paymentCurrencyId;

      // Get documents array (matching template logic)
      const documents = formValues.documents?.map(({ response }) => response) || [];

      // Get special instructions (matching template field name)

      const purposeOfRemittance = formValues.purposeOfRemittance;
      // Get date of transaction (matching template field name)
      const dateOfTransaction = (formValues as any).send_date ?? formValues.dateOfTransaction ?? dayjs();

      // Get providerWalletId from form field 'providerWallet' (matching payment-request logic)
      // Priority: form field > undefined (if state exists, it should be set to form field)
      const providerWalletIdValue = providerWalletData?.id || (formValues as any).providerWallet || form.getFieldValue('providerWallet') || undefined;

      // Get providerBankAccountId from form field 'providerBankAccount' (matching payment-request logic)
      // Priority: form field > undefined (if state exists, it should be set to form field)
      const providerBankAccountIdValue = providerBankAccountData?.id || (formValues as any).providerBankAccount || form.getFieldValue('providerBankAccount') || undefined;

      const specialInstructions = (formValues as any).special_intructions || formValues.specialInstructions;
      const specialInstructionsDetail = specialInstructions && specialInstructions.startsWith(specialInstructionsDraft)
        ? specialInstructions.slice(specialInstructionsDraft?.length).trim() ?? undefined
        : specialInstructions || undefined;

      const data = {
        companyId: companyData?.id,
        customerId: userId,
        userId,
        dateOfTransaction,
        direction,
        documents,
        exchangeRate,
        feeConfigId,
        feeConfigVersion: configFee?.version,
        feesPayer,
        paymentAmount,
        paymentCurrencyId: paymentCurrencyValue,

        providerWalletId: providerWalletIdValue || undefined,
        providerBankAccountId: providerBankAccountIdValue || undefined,
        receiverAmount: beneficiaryAmount,
        receiveCurrencyId: beneficiaryCurrency,
        serviceProviderId: serviceProviderData?.id,

        specialInstructions: specialInstructionsDraft ?? undefined,
        specialInstructionsDetail,
        status: "PENDING",
        usdToFeeCurrencyRate: configFee?.usdToFeeCurrencyRate,
        purposeOfRemittance,
      };

      createPaymentRequest(
        {
          resource: 'api/v1/payment_orders/create',
          values: data,
          errorNotification: false,
        },
        {
          onSuccess: (response: any) => {
            if (openNotification) {
              openNotification({
                type: 'success',
                message: 'Request money created successfully',
              });
            }
            const createdId = response?.data?.id || response?.data?.data?.id;
            if (createdId) {
              navigate(`/payment-order?editId=${createdId}`);
            } else {
              navigate('/payment-order');
            }
          },
          onError: (error: any) => {
            if (error?.reason_code === 'ERR00104') {
              setShowReviewFee(true)
            } else if (openNotification) {
              openNotification({
                type: 'error',
                message: 'Error creating request money',
                description: error?.message || 'Something went wrong',
              });
            }
          },
        }
      );
    },
    [
      customerData,
      companyData,
      direction,
      beneficiaryCurrencyId,
      paymentCurrencyId,
      sendCurrency,
      paymentCurrency,
      exchangeRate,
      configFee,
      feeConfigId,
      feesPayer,
      createPaymentRequest,
      openNotification,
      navigate,
      paymentAmount,
      beneficiaryAmount,
      serviceProviderData?.id,
      form,
      providerBankAccountData?.id,
      providerWalletData?.id,
      specialInstructionsDraft,
    ],
  );

  const { openNotification: openDifferentCurrenciesNotification, contextHolder: differentCurrenciesContextHolder } =
    useNotificationOkCancel({
      ...confirmModalDifferentCurrenciesProps,
      onOk: () => {
        form.submit();
      },
    });

  const handleSubmit = useCallback(
    (buttonKey: 'save' | 'send') => async () => {
      hasInputPaymentRef.current = true;
      const nextStatus = isAdminRole
        ? form.getFieldValue('status')
        : buttonKey === 'save'
          ? PaymentStatus.DRAFT
          : PaymentStatus.PENDING;
      try {
        setNextStatus(nextStatus);
        if (
          accountData?.currencyCode !== undefined &&
          currencyData?.code !== undefined &&
          accountData?.currencyCode !== currencyData?.code
        ) {
          openDifferentCurrenciesNotification();
        } else {
          form.submit();
        }
      } catch (error) {
        form.submit();
      }
    },
    [isAdminRole, form, accountData?.currencyCode, currencyData?.code, openDifferentCurrenciesNotification],
  );

  const [companyCreateForm] = Form.useForm<ICompany>();
  const {
    close: createCompanyModalClose,
  } = useModalForm({
    autoSubmitClose: true,
    form: companyCreateForm,
    submit: async formValues => {
      const company = await createCompany(formValues).then(res => res.data);
      setCompanyData(company);
      createCompanyModalClose();
    },
  });

  const [bankCreateForm] = Form.useForm<IBank>();
  const {
    close: createBankModalClose,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    show: createBankModalShow,
  } = useModalForm({
    autoSubmitClose: true,
    form: bankCreateForm,
    submit: () => {
      createBankModalClose();
    },
  });

  const [bankAccountCreateForm] = Form.useForm<IBankAccount>();
  const {
    close: createBankAccountModalClose,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    show: createBankAccountModalShow,
  } = useModalForm({
    autoSubmitClose: true,
    form: bankAccountCreateForm,
    submit: () => {
      createBankAccountModalClose();
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const companyFilter = useMemo(
    (): CrudFilter => ({
      field: 'companyId',
      operator: 'eq',
      value: companyData?.id ?? -1,
    }),
    [companyData],
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const bankFilter = useMemo(
    (): CrudFilter => ({
      field: 'bankId',
      operator: 'eq',
      value: bankData?.id ?? -1,
    }),
    [bankData],
  );

  const onUnsavedChangesOk = useCallback(() => {
    form.resetFields();
    clearCurrencyData();
    clearCompanyData();
    clearBankData();
    clearBankAccountData();
    navigate('/payment-order');
    setAmountData(null);
    setInstructionsData(undefined);
    setFileList([]);
  }, [clearBankAccountData, clearBankData, clearCompanyData, clearCurrencyData, navigate, form]);

  const { openNotification: openUnsavedChangesNotification, contextHolder: unsavedChangesContextHolder } =
    useNotificationOkCancel({
      ...confirmModalUnsavedChangesProps,
      onOk: onUnsavedChangesOk,
    });

  const handleCancel = useCallback(() => {
    const beneficiaryValue = form.getFieldValue('beneficiary_value');
    const paymentValue = form.getFieldValue('payment_value');
    const beneficiaryCurrency = form.getFieldValue('beneficiary_currency');
    const paymentCurrency = form.getFieldValue('payment_currency');
    const isFullFields = currencyCode || bankData || companyData || accountData || amountData || instructionsData ||
      beneficiaryValue || paymentValue || beneficiaryCurrency || paymentCurrency;
    if (isFullFields) {
      openUnsavedChangesNotification();
    } else {
      onUnsavedChangesOk();
    }
  }, [
    currencyCode,
    bankData,
    companyData,
    accountData,
    amountData,
    instructionsData,
    form,
    openUnsavedChangesNotification,
    onUnsavedChangesOk,
  ]);

  const onChangeUpload = useCallback((info: UploadChangeParam) => {
    const { file, fileList: newFileList } = info;

    // Update uploading status
    setIsUploading(file.status === 'uploading' || file.status === 'error');

    // Update file list
    setFileList(newFileList);

    // Validate file count (max 5 files)
    const MAX_FILE_COUNT = 5;
    if (newFileList.length > MAX_FILE_COUNT) {
      api['error']({
        message: 'File limit exceeded',
        description: `You can upload up to ${MAX_FILE_COUNT} files only!`,
      });
      // Remove excess files
      const validFileList = newFileList.slice(0, MAX_FILE_COUNT);
      setFileList(validFileList);
      form.setFieldValue('documents', validFileList);
      return;
    }

    // Update form field
    form.setFieldValue('documents', newFileList);

    // Validate file list
    form.validateFields(['documents']);
  }, [api, form]);

  const handleRemoveFile = useCallback((file: UploadFile) => {
    const newFileList = fileList.filter(item => item.uid !== file.uid);
    setFileList(newFileList);
    form.setFieldValue('documents', newFileList.length > 0 ? newFileList : undefined);
    form.validateFields(['documents']);
  }, [fileList, form]);

  useEffect(() => {
    form.resetFields(['abaRoutingNumber', 'bankStateBranchNumber', 'branchNumber', 'iban']);
  }, [bankData, form]);

  return (
    <>
      {differentCurrenciesContextHolder}
      {unsavedChangesContextHolder}
      <div style={{ padding: '16px', maxWidth: '100%', backgroundColor: '#fff' }}>
        <Typography.Title level={3} style={{ marginBottom: 24 }}>
          Create Request Money
        </Typography.Title>
        <Form
          form={form}
          layout='vertical'
          onFinish={onSubmit}
          disabled={isCreating}
          validateTrigger={false}
          onValuesChange={(changedValues, allValues) => {
            const changedFieldName = Object.keys(changedValues)[0];

            // Don't clear validation errors when changing currency
            if (isChangingCurrency) {
              return;
            }

            // IMPORTANT: Never clear errors when changing currency fields
            // Currency changes will trigger their own validation logic
            if (changedFieldName === 'payment_currency' || changedFieldName === 'beneficiary_currency') {
              // If payment_currency changed and we have a preserved error, ensure it's set
              if (changedFieldName === 'payment_currency' && preservedBeneficiaryError) {
                const currentErrors = form.getFieldError('beneficiary_value');
                if (currentErrors.length === 0) {
                  // Set the preserved error to ensure it's displayed
                  form.setFields([{
                    name: 'beneficiary_value',
                    errors: [preservedBeneficiaryError]
                  }]);
                }
              }
              handleFormValuesChange(changedValues, allValues);
              return;
            }

            // Check if beneficiary_value or payment_value has errors
            // NEVER clear errors if field still has validation errors
            const beneficiaryErrors = form.getFieldError('beneficiary_value');
            const paymentErrors = form.getFieldError('payment_value');
            const hasBeneficiaryError = beneficiaryErrors.length > 0;
            const hasPaymentError = paymentErrors.length > 0;

            // If beneficiary_value or payment_value is being changed and has errors, don't clear
            if (changedFieldName === 'beneficiary_value' || changedFieldName === 'payment_value') {
              if (hasBeneficiaryError || hasPaymentError) {
                handleFormValuesChange(changedValues, allValues);
                return;
              }
            }

            // If changing other fields, check if beneficiary_value or payment_value has errors
            // If they have errors, don't clear them (only clear errors for the changed field if it's not beneficiary_value or payment_value)
            if (hasBeneficiaryError || hasPaymentError) {
              // Only clear errors for fields that are not beneficiary_value or payment_value
              const fieldsToClear: string[] = [];
              Object.keys(changedValues).forEach(fieldName => {
                if (fieldName !== 'beneficiary_value' && fieldName !== 'payment_value') {
                  fieldsToClear.push(fieldName);
                }
              });
              if (fieldsToClear.length > 0) {
                // Only clear errors for non-beneficiary/payment fields
                const filteredChangedValues: any = {};
                fieldsToClear.forEach(fieldName => {
                  filteredChangedValues[fieldName] = changedValues[fieldName];
                });
                clearFieldErrorMessageOnChange(form)(filteredChangedValues);
              }
              handleFormValuesChange(changedValues, allValues);
              return;
            }

            clearFieldErrorMessageOnChange(form)(changedValues);
            handleFormValuesChange(changedValues, allValues);
          }}
        >
          {/* Customer Selection - REQUIRED FIRST - Always at the top */}
          {/* All other fields are disabled until customer is selected */}
          <Form.Item
            label="Customer"
            name='customerId'
            rules={[
              ...customerRules(customerData),
              {
                required: true,
                message: 'Please select a customer first',
              },
            ]}
            style={{ marginBottom: 24 }}
            required
          >
            <AutocompleteCustomerInput
              onSelect={onSelectCustomer}
              refetchOnEmptyValue
              onClear={clearCustomerData}
              allowClear
            />
          </Form.Item>
          <Details
            show={showCustomerDetails}
            setShow={setShowCustomerDetails}
            name={'customer'}
            fields={customerDetailsFieldsSmartUID}
            data={customerData}
          />

          {/* Rest of the form - disabled until customer is selected */}
          {!customerData && (
            <Alert
              message="Please select a customer first"
              description="You must select a customer before filling in the payment request details."
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}

          {/* Hidden beneficiary_value field - automatically synced with payment_value */}
          <Form.Item name='beneficiary_value' hidden>
            <Input />
          </Form.Item>
          <Form.Item name='beneficiary_currency' hidden>
            <Input />
          </Form.Item>

          {/* Payment Amount and Currency - matching template */}
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 16, marginBottom: 16 }}>
            <Form.Item
              label="You get"
              name='payment_value'
              rules={[
                { required: true, message: 'This field is required' },
                {
                  validator: async (_, value) => {
                    if (!hasInputPaymentRef.current) {
                      return Promise.resolve()
                    }
                    if (!value) {
                      return Promise.resolve();
                    }

                    // Allow incomplete values (like "." or "0.") during typing
                    // Only validate complete numbers
                    if (value === '.' || (value.endsWith('.') && !value.startsWith('.'))) {
                      return Promise.resolve();
                    }

                    // Check if value is a valid number
                    const numValue = Number(value);
                    if (isNaN(numValue) || numValue <= 0) {
                      return Promise.reject(
                        new Error('The amount must be greater than 0')
                      );
                    }

                    // Max limit check theo currency - use form field value to ensure we use the latest currency
                    const currentPaymentCurrency = form.getFieldValue('payment_currency') || paymentCurrency || paymentCurrencyId;
                    const maxLimit = getMaxLimit(currentPaymentCurrency);

                    if (maxLimit !== null && numValue > maxLimit) {
                      return Promise.reject(
                        new Error(
                          `Amount exceeds the maximum allowed limit of ${formatNumber(
                            maxLimit,
                            paymentDecimal
                          )}`
                        )
                      );
                    }

                    // Fees payable by is always BENEFICIARY (matching template)
                    // Min amount validation for beneficiary pays fee
                    {
                      // Skip min amount validation if configFee is not available or loading
                      // This prevents validation with old fee data when currency is changed
                      if (!configFee || loadingFee) {
                        // Use preserved error if:
                        // 1. We're actively changing currency, OR
                        // 2. configFee is null/loading and we have a preserved error (currency was just changed)
                        // This ensures errors are preserved during the entire currency change process
                        if (preservedBeneficiaryErrorRef.current && (isChangingCurrencyRef.current || !configFee)) {
                          console.log('[FLOW] Validation - Using preserved error (payment_value, BENEFICIARY pays):', preservedBeneficiaryErrorRef.current);
                          return Promise.reject(new Error(preservedBeneficiaryErrorRef.current));
                        }
                        // If there's an existing error, keep it (don't clear)
                        const currentErrors = form.getFieldError('payment_value');
                        if (currentErrors.length > 0) {
                          console.log('[FLOW] Validation - Keeping existing error (payment_value, BENEFICIARY pays):', currentErrors[0]);
                          return Promise.reject(new Error(currentErrors[0]));
                        }
                        return Promise.resolve();
                      }

                      // If serviceFee is null, skip validation to avoid false positives
                      // The validation will be triggered again when fees are calculated
                      if (serviceFee === null) {
                        // Use preserved error if:
                        // 1. We're actively changing currency, OR
                        // 2. Fees are null and we have a preserved error (currency was just changed)
                        if (preservedBeneficiaryErrorRef.current && (isChangingCurrencyRef.current || serviceFee === null)) {
                          console.log('[FLOW] Validation - Using preserved error (fees not ready, payment_value):', preservedBeneficiaryErrorRef.current);
                          return Promise.reject(new Error(preservedBeneficiaryErrorRef.current));
                        }
                        // If there's an existing error, keep it (don't clear)
                        const currentErrors = form.getFieldError('payment_value');
                        if (currentErrors.length > 0) {
                          console.log('[FLOW] Validation - Keeping existing error (fees not ready, payment_value):', currentErrors[0]);
                          return Promise.reject(new Error(currentErrors[0]));
                        }
                        // Don't validate yet, wait for fees to be calculated
                        // Return resolve to avoid blocking, but validation will be retried when fees are ready
                        return Promise.resolve();
                      }

                      // Since feesPayer is always BENEFICIARY, validate min amount
                      const minAmount = roundTo(
                        configFee?.bufferUsd *
                        configFee?.usdToFeeCurrencyRate,
                        paymentDecimal
                      );

                      if (numValue < minAmount) {
                        setPaymentValueError(true);
                        const errorMsg = `You get must be at least ${formatNumber(minAmount, paymentDecimal)}`;
                        console.log('[FLOW] Validation - Min amount validation failed (payment_value, BENEFICIARY pays):', errorMsg);
                        return Promise.reject(new Error(errorMsg));
                      }
                    }

                    setPaymentValueError(false);
                    // Clear preserved error since validation passed
                    preservedBeneficiaryErrorRef.current = null;
                    return Promise.resolve();
                  },
                },
              ]}
              style={{ width: '48%' }}
              validateTrigger='onBlur'
            >
              <Input
                placeholder='You get'
                size='middle'
                style={inputFormStyles}
                onChange={handleChangePaymentValue}
                onBlur={handleBlurPayment}
                onKeyDown={(e) => {
                  const allowedKeys = [
                    'Backspace',
                    'Delete',
                    'ArrowLeft',
                    'ArrowRight',
                    'Tab',
                  ];
                  if (allowedKeys?.includes(e.key)) return;

                  if (!/^[0-9.]$/.test(e.key)) {
                    e.preventDefault();
                    return;
                  }

                  const { value, selectionStart } = e.currentTarget;

                  if (e.key === '.' && value?.includes('.')) {
                    e.preventDefault();
                    return;
                  }

                  if (value?.includes('.')) {
                    const dotIndex = value?.indexOf('.');
                    const decimals = value?.substring(dotIndex + 1);

                    if (
                      selectionStart !== null &&
                      selectionStart > dotIndex &&
                      decimals.length >= paymentDecimal
                    ) {
                      e.preventDefault();
                    }
                  }
                }}
                disabled={isFormDisabled}
              />
            </Form.Item>
            <Form.Item
              label="Currency"
              name='payment_currency'
              rules={currencyRules(paymentCurrencyData, nextStatus)}
              style={{
                width: '48%',
              }}
            >
              <AutoComplete
                enableNotHasData={true}
                style={{
                  marginTop: '0px',
                }}
                resource='api/v1/asset-config-groups/get-all'
                mapItem={mapCurrencyUserItemWithIcon}
                onSelect={async (_, { data }) => {
                  setIsChangingCurrency(true);
                  isChangingCurrencyRef.current = true;

                  // Preserve payment_value error before clearing configFee
                  const paymentErrors = form.getFieldError('payment_value');
                  const hasMinAmountError = paymentErrors.some((err: string) => err?.includes('must be at least') || err?.includes('You get must be at least'));
                  if (hasMinAmountError) {
                    preservedBeneficiaryErrorRef.current = paymentErrors[0] || null;
                    setPreservedBeneficiaryError(paymentErrors[0] || null);
                  } else {
                    preservedBeneficiaryErrorRef.current = null;
                    setPreservedBeneficiaryError(null);
                  }

                  setConfigFee(null);
                  setServiceFee(null);

                  const currencyId = (data as any).id;
                  if (!currencyId) return;

                  const currency = allCurrencies.find((item: any) => item.id === currencyId);
                  if (currency) {
                    setPaymentCurrencyData(currency);
                    setPaymentCurrencyId(currencyId);
                    setPaymentCurrency(currencyId);
                    setPaymentCurrencySymbol(currency.symbol || currency.code);
                    setPaymentDecimal(currency.decimals || 2);

                    // Update beneficiary currency to match payment currency
                    setCurrencyData(currency);
                    setBeneficiaryCurrencyId(currencyId);
                    setSendCurrency(currencyId);
                    setSendCurrencySymbol(currency.symbol || currency.code);
                    setBeneficiaryDecimal(currency.decimals || 2);

                    const currencySymbol = currency.symbol || currency.code;
                    form.setFieldsValue({
                      payment_currency: currencySymbol,
                      beneficiary_currency: currencySymbol, // Hidden field
                    });
                  }

                  const currentPaymentValue = form.getFieldValue('payment_value');
                  const paymentValueNum = Number(currentPaymentValue) || 0;

                  // Since currencies are the same, exchange rate is always 1
                  setExchangeRate(1);

                  if (paymentValueNum > 0) {
                    const formattedValue = paymentValueNum.toFixed(currency.decimals || 2);
                    form.setFieldsValue({
                      payment_value: formattedValue,
                      beneficiary_value: formattedValue, // Sync hidden field
                    });
                    setPaymentAmount(paymentValueNum);
                    setBeneficiaryAmount(paymentValueNum);
                  } else {
                    // const defaultValue = '1000.00';
                    const defaultValue = '0.00';
                    const defaultValueNum = Number(defaultValue);
                    const formattedValue = defaultValueNum.toFixed(currency.decimals || 2);
                    form.setFieldsValue({
                      payment_value: formattedValue,
                      beneficiary_value: formattedValue, // Sync hidden field
                    });
                    setPaymentAmount(defaultValueNum);
                    setBeneficiaryAmount(defaultValueNum);
                  }

                  setTimeout(() => {
                    setIsChangingCurrency(false);
                    isChangingCurrencyRef.current = false;
                    // Re-validate after fees are recalculated
                    const validateAfterFees = () => {
                      if (feesPayer === 'BENEFICIARY' && serviceFee === null) {
                        // Wait for fees to be calculated
                        setTimeout(validateAfterFees, 100);
                        return;
                      }

                      // Always validate payment_value when currency changes
                      form.validateFields(['payment_value']).then(() => {
                        // Clear preserved error if validation passes
                        preservedBeneficiaryErrorRef.current = null;
                        setPreservedBeneficiaryError(null);
                      }).catch(() => {
                        // Validation errors are expected - trigger validation again to show errors
                        setTimeout(() => {
                          form.validateFields(['payment_value']).catch(() => {
                            // Validation errors are expected - ignore
                          });
                        }, 100);
                      });
                    };

                    setTimeout(validateAfterFees, 200);
                    // Also validate immediately after a short delay
                    setTimeout(() => {
                      form.validateFields(['payment_value']).catch(() => {
                        // Validation errors are expected - ignore
                      });
                    }, 300);
                  }, 150);
                }}
                onClear={() => {
                  clearPaymentCurrencyData();
                  setPaymentCurrencyId(undefined);
                  setPaymentCurrency(undefined);
                }}
                refetchOnEmptyValue
                readOnly={true}
                disabled={isFormDisabled}
              >
                <Input placeholder='Currency' size='large' disabled={isFormDisabled} suffix={<DownOutlined />} />
              </AutoComplete>
            </Form.Item>
          </div>

          <Form.Item
            label="Choose sender"
            name='beneficiary'
            rules={[
              {
                required: true,
                message: 'This field is required',
              },
              ...companyRules(companyData, nextStatus)
            ]}
          >
            <AutoComplete
              enableNotHasData={true}
              resource='api/v1/companies'
              additionalFilters={[
                companySearchFieldNameFilter,
                {
                  field: '_isProvider',
                  operator: 'eq' as const,
                  value: false,
                },
                ...(customerData?.id ? [{
                  field: '_userId',
                  operator: 'eq' as const,
                  value: customerData.id,
                }] : []),
              ]}
              mapItem={mapCompanyUserItem}
              onSelect={onSelectCompany}
              allowClear
              dropdownButtonLabel='View more'
              enableLoadMore={true}
              pageSize={20}
              refetchOnEmptyValue
              onClear={clearCompanyData}
              disabled={isFormDisabled}
              clearOnBlurIfUnselected
            >
              <Input placeholder='Choose sender' size='large' disabled={isFormDisabled} suffix={<DownOutlined />} />
            </AutoComplete>
          </Form.Item>

          {/* Fees payable by is always BENEFICIARY - display fee breakdown */}
          {configFee && (
            <div style={{ marginTop: 16, padding: '16px', backgroundColor: '#f5f5f5', borderRadius: 4, marginBottom: 16 }}>
              <div style={{ marginBottom: 8 }}>
                <Typography.Text style={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.85)' }}>
                  <strong>Transfer fee:</strong> {paymentValue ? `${formatNumber(0, paymentDecimal)} ${paymentCurrencySymbol || paymentCurrencyData?.symbol || paymentCurrencyData?.code || ''}` : `-`}
                </Typography.Text>
              </div>
              <div style={{ marginBottom: 8 }}>
                <Typography.Text style={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.85)' }}>
                  <strong>Service fee:</strong> {paymentValue ? `${formatNumber(serviceFee || 0, paymentDecimal)} ${paymentCurrencySymbol || paymentCurrencyData?.symbol || paymentCurrencyData?.code || ''}` : `-`}
                </Typography.Text>
              </div>
              <div style={{ marginBottom: 8, borderTop: '1px solid #d9d9d9', paddingTop: 8 }}>
                <Typography.Text style={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.85)' }}>
                  <strong>Total fee:</strong> {
                    !hasInputPaymentRef.current
                      ? '-'
                      : paymentValue
                        ? `${formatNumber(serviceFee || 0, paymentDecimal)} ${paymentCurrencySymbol || paymentCurrencyData?.symbol || paymentCurrencyData?.code || ''}`
                        : `-`
                  }
                </Typography.Text>
              </div>
              <div style={{ marginTop: 16, padding: '12px', backgroundColor: '#e6f7ff', borderRadius: 4 }}>
                <Typography.Text style={{ fontSize: 16, fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.85)', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Total to pay:</span>
                  <span style={{ color: '#F28A28' }}>
                    {!hasInputPaymentRef.current
                      ? '-'
                      : paymentValue
                        ? `${formatNumber(Number(paymentValue), paymentDecimal)} ${paymentCurrencySymbol || paymentCurrencyData?.symbol || paymentCurrencyData?.code || ''}`
                        : `-`
                    }
                  </span>
                </Typography.Text>
              </div>
              <div style={{ marginTop: 8 }}>
                <Typography.Text style={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.85)' }}>
                  <strong>Beneficiary Will Receive:</strong> <span style={{ color: '#F28A28' }}>
                    {!hasInputPaymentRef.current
                      ? '-'
                      : paymentValue ? `${formatNumber(
                        (Number(paymentValue) - (serviceFee || 0)).toFixed(paymentDecimal),
                        paymentDecimal
                      )
                        } ${paymentCurrencySymbol || paymentCurrencyData?.symbol || paymentCurrencyData?.code || ''}` : `-`}
                  </span>
                </Typography.Text>
              </div>
            </div>
          )}

          <Details
            show={showCompanyDetails}
            setShow={setShowCompanyDetails}
            name={'company'}
            fields={companyBeneficiaryDetailsFields}
            data={companyData}
          />

          {/* Payment Method Provider */}
          <div style={{ marginBottom: 24 }}>
            <Typography style={{ fontSize: 14, fontWeight: 500, marginBottom: 12, color: 'rgba(0, 0, 0, 0.85)' }}>
              Payment Method
            </Typography>
            <Form.Item
              label="Provider"
              name='serviceProviderId'
              rules={[
                {
                  required: true,
                  message: 'This field is required',
                },
                ...paymentRequestServiceProviderRules(serviceProviderData, nextStatus)
              ]}
            >
              <AutoComplete
                enableNotHasData={true}
                resource='api/v1/companies/defaults'
                additionalFilters={[
                  companySearchFieldNameFilter,
                  ...(paymentCurrencyId ? [{
                    field: 'currencyId',
                    operator: 'eq' as const,
                    value: paymentCurrencyId,
                  }] : []),
                ]}
                mapItem={mapCompanyItem}
                onSelect={onSelectServiceProvider}
                allowClear
                refetchOnEmptyValue
                onClear={clearServiceProviderData}
                disabled={isFormDisabled}
              >
                <Input placeholder='Service provider' size='large' disabled={isFormDisabled} suffix={<DownOutlined />} />
              </AutoComplete>
            </Form.Item>
            <Details
              show={showServiceProviderDetails}
              setShow={setShowServiceProviderDetails}
              name={'provider'}
              fields={companyDetailsFields}
              data={serviceProviderData}
            />
            {/* Provider Bank Account or Wallet - only show when provider is selected */}
            {serviceProviderData?.id && (
              <>
                {/* Show Bank Account for FIAT currency */}
                {paymentCurrencyData?.type === 'FIAT' ? (
                  <Form.Item
                    label="Provider Bank Account"
                    name='providerBankAccount'
                    rules={[
                      {
                        required: true,
                        message: 'This field is required',
                      },
                    ]}
                  >
                    <AutoComplete
                      clearOnBlurIfUnselected
                      enableNotHasData={true}
                      resource={`api/v1/bank_accounts/companies/${serviceProviderData.id}/get-list`}
                      disabled={!serviceProviderData?.id || !customerData?.id || isFormDisabled}
                      additionalFilters={[
                        ...(paymentCurrencyId ? [{
                          field: '_currencyId',
                          operator: 'eq' as const,
                          value: paymentCurrencyId,
                        }] : []),
                        ...(customerData?.id ? [{
                          field: '_userId',
                          operator: 'eq' as const,
                          value: customerData.id,
                        }] : []),
                      ]}
                      mapItem={(item: any, index: number) => ({
                        value: item?.id || index,
                        key: item?.id || index,
                        label: (
                          <Typography.Text>
                            {item?.accountNumber ? `${item.accountNumber} [${item.currencyCode || ''}]` : `Item ${index + 1}`}
                          </Typography.Text>
                        ),
                        data: item,
                      })}
                      onSelect={onSelectProviderBankAccount}
                      allowClear
                      refetchOnEmptyValue
                      onClear={clearProviderBankAccountData}
                      enableLoadMore={true}
                      pageSize={10}
                      dropdownButtonLabel='View more'
                    >
                      <Input placeholder='Provider Bank Account' size='large' disabled={isFormDisabled} suffix={<DownOutlined />} />
                    </AutoComplete>
                  </Form.Item>
                ) : (
                  /* Show Wallet for CRYPTO currency */
                  <Form.Item
                    label="Provider Wallet Address"
                    name='providerWallet'
                    rules={[
                      {
                        required: true,
                        message: 'This field is required',
                      },
                    ]}
                  >
                    <AutoComplete
                      clearOnBlurIfUnselected
                      enableNotHasData={true}
                      resource={`api/v1/company-wallets/companies/${serviceProviderData.id}/get-list`}
                      disabled={!serviceProviderData?.id || !customerData?.id || isFormDisabled}
                      additionalFilters={[
                        ...(paymentCurrencyId ? [{
                          field: '_currencyId',
                          operator: 'eq' as const,
                          value: paymentCurrencyId,
                        }] : []),
                        ...(customerData?.id ? [{
                          field: '_userId',
                          operator: 'eq' as const,
                          value: customerData.id,
                        }] : []),
                      ]}
                      mapItem={(item: any, index: number) => ({
                        value: item?.id,
                        key: item?.id,
                        label: (
                          <Typography.Text>
                            {item?.address || item?.name || `Item ${index + 1}`}
                          </Typography.Text>
                        ),
                        data: item,
                      })}
                      onSelect={onSelectProviderWallet}
                      allowClear
                      refetchOnEmptyValue
                      onClear={clearProviderWalletData}
                      enableLoadMore={true}
                      pageSize={10}
                      dropdownButtonLabel='View more'
                    >
                      <Input placeholder='Provider Wallet Address' size='large' disabled={isFormDisabled} suffix={<DownOutlined />} />
                    </AutoComplete>
                  </Form.Item>
                )}
                {/* Show details when selected */}
                {providerBankAccountData && paymentCurrencyData?.type === 'FIAT' && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Typography style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.85)' }}>
                      Currency: {providerBankAccountData?.currencyCode}
                    </Typography>
                    <Typography style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.85)' }}>
                      Account name: {serviceProviderData?.name}
                    </Typography>
                    {providerBankAccountData?.bank && (
                      <>
                        <Typography style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.85)' }}>
                          Bank name: {providerBankAccountData.bank.name}
                        </Typography>
                        {providerBankAccountData.bank.swift && (
                          <Typography style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.85)' }}>
                            SWIFT: {providerBankAccountData.bank.swift}
                          </Typography>
                        )}
                        {providerBankAccountData.bank.legalAddress && (
                          <Typography style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.85)' }}>
                            Bank Address: {providerBankAccountData.bank.legalAddress}
                          </Typography>
                        )}
                      </>
                    )}
                  </div>
                )}
                {providerWalletData && paymentCurrencyData?.type === 'CRYPTO' && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Typography style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.85)' }}>
                      Currency: {providerWalletData?.assetConfig?.symbol}
                    </Typography>
                    <Typography style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.85)' }}>
                      Network: {providerWalletData?.network?.name}
                    </Typography>
                  </div>
                )}
              </>
            )}
          </div>

          <SelectField
            label="Purpose Of Remittance"
            name="purposeOfRemittance"
            getOptionLabel={getPurposeOfRemittanceOptionLabel}
            items={purposeOfRemittanceItems}
            placeholder={purposeOfRemittancePlaceholder}
            allowClear
            rules={[{ required: true, message: "This field is required" }]}
            disabled={isFormDisabled}
          />
          <Form.Item
            label="Special instructions"
            name='specialInstructions'
            validateTrigger='onBlur'
            rules={[
              {
                required: true,
                message: 'This field is required',
              },
              {
                max: 256,
                message: 'Maximum 256 characters',
                validateTrigger: 'onBlur',
              },
            ]}
          >
            <Input.TextArea
              rows={1}
              placeholder='Special instructions:'
              onChange={(e) => {
                const currentValue = e.target.value;
                const idUserPrefix = `Ref ${customerData?.smartUID}` || '';
                if (!currentValue.startsWith(idUserPrefix)) {
                  const finalValue = idUserPrefix;
                  form.setFieldValue('specialInstructions', finalValue);
                  setInstructionsData(finalValue);
                } else {
                  form.setFieldValue('specialInstructions', currentValue);
                  setInstructionsData(currentValue);
                }
              }}
              allowClear
              disabled={isFormDisabled}
            />
          </Form.Item>
          <Col>
            <Form.Item
              label="Supporting documents"
              name='documents'
              valuePropName='fileList'
              getValueFromEvent={getFileListFromEvent}
              rules={[
                {
                  validator: async (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.resolve();
                    }

                    // Validate file count
                    const MAX_FILE_COUNT = 5;
                    if (value.length > MAX_FILE_COUNT) {
                      return Promise.reject(new Error(`Maximum ${MAX_FILE_COUNT} files allowed`));
                    }

                    // Validate file types (images, PDF, Word, Excel — same as beforeUpload)
                    const invalidFiles = value.filter((file: UploadFile) => {
                      const fileType = file.type || (file.originFileObj as any)?.type;
                      return fileType && !allowedUploadTypes.includes(fileType);
                    });

                    if (invalidFiles.length > 0) {
                      return Promise.reject(
                        new Error(
                          'Allowed: images (JPG, PNG, WEBP, GIF, BMP, TIFF), PDF, Word (.doc, .docx), Excel (.xls, .xlsx)',
                        ),
                      );
                    }

                    // Validate file sizes (10MB max)
                    const MAX_SIZE = 10 * 1024 * 1024;
                    const oversizedFiles = value.filter((file: UploadFile) => {
                      const fileSize = file.size || (file.originFileObj as any)?.size;
                      return fileSize && fileSize > MAX_SIZE;
                    });

                    if (oversizedFiles.length > 0) {
                      return Promise.reject(new Error('File size must be less than 10 MB'));
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Upload
                accept={acceptUploadFileTypes}
                action={`${ROOT_URL}/api/v1/storage/upload`}
                listType='text'
                headers={{
                  Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
                }}
                multiple={true}
                beforeUpload={beforeUpload}
                onChange={onChangeUpload}
                onRemove={handleRemoveFile}
                fileList={fileList}
                disabled={isFormDisabled}
                showUploadList={{
                  showRemoveIcon: !isFormDisabled,
                }}
              >
                <Button icon={<UploadOutlined />} style={uploadButtonStyle} disabled={isFormDisabled || fileList.length >= 5}>
                  &nbsp;Click to upload supporting documents
                </Button>
              </Upload>
            </Form.Item>
            {fileList.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Uploaded files ({fileList.length}/5):
                </Typography.Text>
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {fileList.map((file) => (
                    <div
                      key={file.uid}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 8px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: 4,
                      }}
                    >
                      <Typography.Text
                        style={{
                          fontSize: 12,
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={file.name}
                      >
                        {file.name || 'Unknown file'}
                      </Typography.Text>
                      {!isFormDisabled && (
                        <Button
                          type="link"
                          danger
                          size="small"
                          onClick={() => handleRemoveFile(file)}
                          style={{ padding: 0, marginLeft: 8 }}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              Maximum 5 files allowed. Supported formats: images, PDF, Word, Excel. Max size: 10 MB per file.
            </Typography.Text>
          </Col>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
            <Button onClick={handleCancel} disabled={isCreating}>
              Cancel
            </Button>
            <Button
              type='primary'
              onClick={handleSubmit('save')}
              loading={isCreating}
              disabled={isFormEmpty || isUploading || isFormDisabled || beneficiaryValueError || paymentValueError}
            >
              Submit Order
            </Button>
            {/* {!isAdminRole && (
              <Button
                type='primary'
                onClick={handleSubmit('send')}
                loading={isCreating}
                disabled={isFormEmpty || isUploading || isFormDisabled || beneficiaryValueError || paymentValueError}
              >
                Send
              </Button>
            )} */}
          </div>
        </Form>
      </div>
      {alertContextHolder}
      <PaymentConfirmationModal
        modalOpened={paymentConfirmationModalOpened}
        closeModal={() => {
          if (paymentRejected.current) {
            setPaymentConfirmationModalOpened(false);
            paymentRejected.current();
          }
        }}
        paymentDetails={{
          fee,
          userWallet,
          ethPrice,
          walletToTransferFundsTo: form?.getFieldValue('cryptoWallet'),
          tokensToTransfer: form?.getFieldValue('amount'),
        }}
        onSubmit={submitPayment}
      />

      <Modal
        open={!!showReviewFee}
        title="Review Fee"
        className='smartModalSM'
        closeIcon={false}
        onOk={() => {
          if (beneficiaryCurrencyId) {
            fetchConfigurationFee(beneficiaryCurrencyId);
          }
          setShowReviewFee(false)
        }}
        cancelButtonProps={{
          style: {
            display: 'none',
          },
        }}
      >
        <p>Fee or beneficiary receive has been updated. Take a moment to review before continuing.</p>
      </Modal>
    </>
  );
};

// Helper hooks and functions for Company Default
export const useMetadataCompaniesDefault = (params: {
  currencyId: number | undefined;
  searchName?: string;
}) => {
  const { data = { data: [] }, isLoading } = useGetCompanyDefault({
    currencyId: params?.currencyId,
    name: params?.searchName,
  });

  return { dataCompaniesDefault: data?.data, isLoading };
};

export const useGetCompanyDefault = (params: {
  currencyId: number | undefined;
  name?: string;
}) => {
  return useQuery<{ data: any }>(
    [`/api/v1/companies/defaults`, params?.currencyId, params?.name],
    async () => getCompanyDefault(params),
    {
      enabled: !!params?.currencyId,
    }
  );
};

export const useGetCompanyDefaultMutation = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: getCompanyDefault,
  });
};

export function getCompanyDefault(params: {
  currencyId: number | undefined;
  name?: string;
}) {
  const param = new URLSearchParams();
  if (params?.currencyId)
    param.append('currencyId', String(params?.currencyId));
  if (params.name) param.append('_like', params.name);
  return httpClient.get(
    `${ROOT_URL}/api/v1/companies/defaults?${param.toString()}`
  );
}

// Helper hooks for Wallet and Bank Account Provider
export const useWalletManagementProvider = (
  companyId: number,
  params: { start: number; end: number },
  name?: string,
  currencyId?: number
) => {
  const {
    data = {
      data: {
        data: { total: 0, pageSize: 10, page: 1, totalPages: 1, items: [] },
      },
    },
    isLoading,
    refetch,
  } = useGetListWalletProvider({
    companyId,
    ...params,
    name,
    currencyId,
  });
  return { dataListWalletProvider: data.data.data, isLoading, refetch };
};

export const useGetListWalletProvider = (params: {
  companyId: number;
  start: number;
  end: number;
  name?: string;
  currencyId?: number;
}) => {
  return useQuery<{
    data: {
      data: {
        total: number;
        page: number;
        pageSize: number;
        totalPages: number;
        items: [];
      };
    };
  }>(
    [
      `/api/v1/company-wallets/companies/${params.companyId}/get-list`,
      params.start,
      params.end,
      params.name,
      params.currencyId,
    ],
    async () => getListWalletProvider(params),
    {
      enabled: !!params.companyId,
    }
  );
};

export function getListWalletProvider(params: {
  companyId: number;
  start: number;
  end: number;
  name?: string;
  currencyId?: number;
}) {
  const param = new URLSearchParams();
  param.append('_start', String(params.start));
  param.append('_end', String(params.end));
  if (params.name) param.append('_like', params.name);
  if (params.currencyId) param.append('_currencyId', String(params.currencyId));
  return httpClient.get(
    `${ROOT_URL}/api/v1/company-wallets/companies/${params.companyId}/get-list?${param.toString()}`
  );
}

export const useBankAccountManagementProvider = (
  companyId: number,
  params: { start: number; end: number },
  name?: string,
  currencyId?: number
) => {
  const {
    data = {
      data: {
        data: { total: 0, pageSize: 10, page: 1, totalPages: 1, items: [] },
      },
    },
    isLoading,
    refetch,
  } = useGetListBankAccountProvider({
    companyId,
    ...params,
    name,
    currencyId,
  });
  return { dataListBankAccountProvider: data.data.data, isLoading, refetch };
};

export const useGetListBankAccountProvider = (params: {
  companyId: number;
  start: number;
  end: number;
  name?: string;
  currencyId?: number;
}) => {
  return useQuery<{
    data: {
      data: {
        total: number;
        page: number;
        pageSize: number;
        totalPages: number;
        items: [];
      };
    };
  }>(
    [
      `/api/v1/bank_accounts/companies/${params.companyId}/get-list`,
      params.start,
      params.end,
      params.name,
      params.currencyId,
    ],
    async () => getListBankAccountProvider(params),
    {
      enabled: !!params.companyId,
    }
  );
};

export function getListBankAccountProvider(params: {
  companyId: number;
  start: number;
  end: number;
  name?: string;
  currencyId?: number;
}) {
  const param = new URLSearchParams();
  param.append('_start', String(params.start));
  param.append('_end', String(params.end));
  if (params.name) param.append('_like', params.name);
  if (params.currencyId) param.append('_currencyId', String(params.currencyId));
  return httpClient.get(
    `${ROOT_URL}/api/v1/bank_accounts/companies/${params.companyId}/get-list?${param.toString()}`
  );
}

